import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as yup from 'yup';
import PhoneInput from 'react-phone-input-2';

import { api, useAPIContext } from '../../api/context';
import { Title } from '../../utils/title';
import toast from '../../utils/toast';
import wizard from '../../utils/wizard';
import { isUsCaPhone, PHONE_COUNTRY_ERROR } from '../../utils/phone';
import Layout from './Layout';
import styles from './_02_Phone.module.scss';

const _02_Phone = () => {

  const { token } = useAPIContext();
  const [ loading, setLoading ] = useState(false);

  const [ initialPhone, setInitialPhone ] = useState('');
  const [ codeSent, setCodeSent ] = useState(false);

  const navigate = useNavigate();

  const next = () => {
    wizard.fetchStatus(token).then(({ missing }) => {
      navigate(wizard.nextAfter('phone', missing));
    });
  };

  /*
   * Pre-fill the phone field from the user's current profile so they
   * can either confirm the existing number or correct it.
   */

  useEffect(() => {
    if (!token) {
      return;
    }

    api(token).v1.profile.query()
      .then(res => {
        if (res && res.body && res.body.data) {
          setInitialPhone(res.body.data.phone || '');
        }
      })
      .catch(err => {
        toast.show(toast.KIND.WARNING, err);
      });
  }, [token]);

  const send = (phone) => {

    if (loading) {
      return;
    }

    if (!isUsCaPhone(phone)) {
      toast.show(toast.KIND.ERROR, PHONE_COUNTRY_ERROR);
      return;
    }

    setLoading(true);

    api(token).v1.profile.phone
      .post({ phone })
      .then(res => {
        setLoading(false);

        if (res.status === 200) {
          setCodeSent(true);
          toast.show(toast.KIND.SUCCESS, 'Confirmation code sent.');
        }

        if (res.status === 400) {
          toast.show(toast.KIND.ERROR, res.body);
        }
      })
      .catch(err => {
        setLoading(false);
        toast.show(toast.KIND.WARNING, err);
      });
  };

  return (
    <>
      <Title value='Phone' />
      <Layout step='phone' auth={true}>
        <div className={styles.root}>

          <Formik
            enableReinitialize={true}
            initialValues={{
              phone: initialPhone,
              code: '',
            }}
            validationSchema={yup.object({
              phone: yup.string()
                .required('Phone required')
                .test('us-ca', PHONE_COUNTRY_ERROR, isUsCaPhone),
              code: yup.string(),
            })}
            onSubmit={(values, actions) => {

              if (loading) {
                return;
              }

              if (!codeSent) {
                send(values.phone);
                return;
              }

              if (!values.code) {
                toast.show(toast.KIND.ERROR, 'Please enter the confirmation code.');
                return;
              }

              setLoading(true);

              api(token).v1.profile.phone.confirm
                .post({ code: values.code })
                .then(res => {
                  setLoading(false);

                  if (res.status === 200) {
                    next();
                  }

                  if (res.status === 400) {
                    toast.show(toast.KIND.ERROR, res.body);
                  }
                })
                .catch(err => {
                  setLoading(false);
                  toast.show(toast.KIND.WARNING, err);
                });

            }} >

            {
              ({ values, handleChange }) => (

                <Form>
                  <div>
                    <PhoneInput
                      containerClass={styles.phone}
                      autoFocus={true}
                      aria-label='phone'
                      inputProps={{ name: 'phone' }}
                      disabled={loading}

                      autoFormat={true}
                      countryCodeEditable={false}
                      country={'us'}
                      disableDropdown={true}
                      specialLabel={'Mobile Phone'}

                      value={values.phone}
                      onChange={(value) => {
                        if (codeSent) {
                          setCodeSent(false);
                        }
                        handleChange({
                          target: {
                            name: 'phone',
                            value: `+${value}`,
                          },
                        });
                      }}
                    />
                    <ErrorMessage
                      name='phone'
                      component='div'
                    />
                  </div>

                  {
                    codeSent && (
                      <div>
                        <label htmlFor='code'>We sent you a verification code via text message 📱. Please check your phone.</label>
                        <Field
                          id='code'
                          aria-label='code'
                          name='code'
                          type='text'
                          disabled={loading}
                        />
                        <ErrorMessage
                          name='code'
                          component='div'
                        />
                        <button
                          type='button'
                          disabled={loading}
                          onClick={() => send(values.phone)}>

                          Re-send Code

                        </button>
                      </div>
                    )
                  }

                  <button
                    type='submit'
                    disabled={loading}>

                    Confirm

                  </button>
                </Form>
              )
            }

          </Formik>

        </div>
      </Layout>
    </>
  );
};

export default _02_Phone;
